/**
 * FailureScreen — Execução com falha (F3-FE-3).
 *
 * Porta a FailureScreen do protótipo.
 * Quando uma execução falha no Compliance Check pós-geração, esta tela:
 *   1. Exibe os checks reprovados com descrição da falha
 *   2. Mostra causa provável e plano de ação (3 passos)
 *   3. Oferece ações: Reexecutar do ponto de falha / Reexecutar do início / Ajustar / Logs / Descartar
 *
 * Fonte de dados: GET /executions/{id}/output (compliance_checks com status === 'failed')
 *                 GET /executions/{id} (status, project_name)
 *
 * Segurança: NUNCA exibe wallet_txn_id. 403 → ForbiddenBanner.
 * Acessibilidade: role="alert" no banner de falha. Foco de teclado visível.
 * Standards: Clean, NFR (a11y), OWASP A09.
 */

import { MOSTRAR_ENGINEERING_STANDARDS } from '@/lib/engineeringStandards';
import { useSearchParams } from 'react-router-dom';
import {
  XCircle, AlertTriangle, RefreshCw, Settings, FileText,
  Trash2, GitBranch, ChevronRight,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, ForbiddenBanner } from '@/components/ui';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useExecution, useExecutionOutput } from '@/api/executions';
import { useNavigationContext } from '@/navigation';

// ─── Sub-componentes ──────────────────────────────────────────────────────────

/**
 * FailedCheckItem — card de um check reprovado.
 */
function FailedCheckItem({ check }) {
  return (
    <div style={{
      display:         'flex',
      alignItems:      'flex-start',
      gap:             12,
      padding:         '14px 16px',
      borderRadius:    8,
      backgroundColor: `${C.hot}0C`,
      border:          `1px solid ${C.hot}44`,
    }}>
      <XCircle size={16} aria-hidden="true" style={{ color: C.hot, flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.neutral, marginBottom: 4 }}>
          {check.standard ?? check.rule_name}
        </div>
        {(check.evidence ?? check.detail) && (
          <div style={{ fontSize: 12, color: C.neutralDim, lineHeight: 1.5 }}>
            {check.evidence ?? check.detail}
          </div>
        )}
      </div>
      <span style={{
        fontSize:        10,
        fontWeight:      700,
        padding:         '2px 7px',
        borderRadius:    4,
        backgroundColor: `${C.hot}22`,
        border:          `1px solid ${C.hot}55`,
        color:           C.hot,
        textTransform:   'uppercase',
        letterSpacing:   '0.04em',
        flexShrink:      0,
      }}>
        REPROVADO
      </span>
    </div>
  );
}

/**
 * ActionPlanStep — passo do plano de ação.
 */
function ActionPlanStep({ number, text }) {
  return (
    <div style={{
      display:    'flex',
      alignItems: 'flex-start',
      gap:        12,
    }}>
      <div style={{
        width:           24,
        height:          24,
        borderRadius:    '50%',
        backgroundColor: `${C.quantum}22`,
        border:          `1px solid ${C.quantum}44`,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        flexShrink:      0,
        fontSize:        11,
        fontWeight:      700,
        color:           C.quantum,
      }}>
        {number}
      </div>
      <div style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.55, paddingTop: 3 }}>
        {text}
      </div>
    </div>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

/**
 * FailureScreen — diagnóstico e plano de ação para execuções com falha.
 * Lê executionId de ?execution=<id>.
 */
export function FailureScreen() {
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('execution');
  const { navigate } = useNavigationContext();

  const {
    data: execution,
    isLoading: loadingExec,
    error: errorExec,
  } = useExecution(executionId);

  const {
    data: output,
    isLoading: loadingOutput,
    error: errorOutput,
    refetch,
  } = useExecutionOutput(executionId);

  if (!executionId) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ fontSize: 14, color: C.neutralMuted, textAlign: 'center' }}>
          Nenhuma execução selecionada.
        </div>
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <Btn variant="ghost" size="sm" onClick={() => navigate('dashboard')}>
            Ir ao Dashboard
          </Btn>
        </div>
      </div>
    );
  }

  if (loadingExec || loadingOutput) {
    return <LoadingSpinner label="Carregando diagnóstico de falha..." />;
  }

  // Só bloqueia se a PRÓPRIA execução não carregou; o output (compliance) é
  // opcional para o diagnóstico e não deve derrubar a tela toda.
  const anyError = errorExec;
  if (anyError) {
    if (anyError.isForbidden) {
      return (
        <div style={{ padding: 24 }}>
          <ForbiddenBanner detail={anyError.detail} />
        </div>
      );
    }
    return (
      <div style={{ padding: 24 }}>
        <QueryError error={anyError} onRetry={refetch} />
      </div>
    );
  }

  // Checks reprovados — o backend usa `result` (não `status`) e `standard` (não `rule_name`).
  const failedChecks = (output?.compliance_checks ?? []).filter(
    (c) => c.result === 'failed' || c.result === 'fail',
  );
  const projectName  = execution?.project_name ?? 'Execução';
  const progress     = execution?.progress ?? 67;
  const repoName     = execution?.repo ?? '—';

  // Causa provável: os checks reprovados + o erro real da execução (se houver).
  const failReasons = failedChecks.map((c) => c.standard ?? c.rule_name).filter(Boolean);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 20px' }}>

      {/* ── Banner de falha ─────────────────────────────────────────────── */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{
          display:         'flex',
          alignItems:      'flex-start',
          gap:             16,
          padding:         '18px 20px',
          borderRadius:    10,
          backgroundColor: `${C.hot}0F`,
          border:          `1px solid ${C.hot}55`,
          marginBottom:    28,
        }}
      >
        <div style={{
          width:           44,
          height:          44,
          borderRadius:    10,
          backgroundColor: `${C.hot}22`,
          border:          `1px solid ${C.hot}55`,
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          flexShrink:      0,
        }}>
          <XCircle size={22} aria-hidden="true" style={{ color: C.hot }} />
        </div>

        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: C.hot, margin: '0 0 4px' }}>
            Execução com Falha
          </h1>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: C.neutralDim }}>
              {projectName}
            </span>
            {repoName !== '—' && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.neutralMuted }}>
                <GitBranch size={11} aria-hidden="true" />
                {repoName}
              </span>
            )}
            <span style={{ fontSize: 12, color: C.neutralMuted }}>
              Progresso: {progress}%
            </span>
          </div>
        </div>
      </div>

      {/* ── Checks reprovados ────────────────────────────────────────────── */}
      {MOSTRAR_ENGINEERING_STANDARDS && (
      <section aria-labelledby="failed-checks-heading" style={{ marginBottom: 24 }}>
        <h2
          id="failed-checks-heading"
          style={{
            fontSize:      12,
            fontWeight:    700,
            color:         C.neutralMuted,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            margin:        '0 0 14px',
          }}
        >
          Compliance Check — {failedChecks.length > 0 ? `${failedChecks.length} Reprovado${failedChecks.length > 1 ? 's' : ''}` : 'PR não gerado'}
        </h2>

        {failedChecks.length === 0 ? (
          <div style={{
            padding:       24,
            textAlign:     'center',
            fontSize:      13,
            color:         C.neutralMuted,
            border:        `1px dashed ${C.blackBorder}`,
            borderRadius:  8,
          }}>
            Detalhes do compliance não disponíveis. Verifique os logs.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {failedChecks.map((check, i) => (
              <FailedCheckItem key={check.check_id ?? i} check={check} />
            ))}
          </div>
        )}
      </section>
      )}

      {/* ── Erro real da execução (quando a falha não foi de compliance) ──── */}
      {execution?.error && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <AlertTriangle size={14} aria-hidden="true" style={{ color: C.hot }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: C.neutralMuted,
              letterSpacing: '0.07em', textTransform: 'uppercase' }}>
              Erro da execução
            </span>
          </div>
          <pre style={{ margin: 0, fontFamily: 'ui-monospace, monospace', fontSize: 12.5,
            lineHeight: 1.55, color: C.neutralDim, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {String(execution.error)}
          </pre>
        </Card>
      )}

      {/* ── Causa provável (compliance) ─────────────────────────────────── */}
      {MOSTRAR_ENGINEERING_STANDARDS && failReasons.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <AlertTriangle size={14} aria-hidden="true" style={{ color: C.neon }} />
            <span style={{
              fontSize:      11,
              fontWeight:    700,
              color:         C.neutralMuted,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
            }}>
              Causa provável
            </span>
          </div>
          <div style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.6 }}>
            Falha no Compliance Check pós-geração:{' '}
            <strong style={{ color: C.neutral }}>
              {failReasons.join(' e ')}
            </strong>
            {' '}({failedChecks.length} de {(output?.compliance_checks ?? []).length} standards reprovados — PR não gerado).
          </div>
        </Card>
      )}

      {/* ── Plano de ação ───────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 28 }}>
        <div style={{
          fontSize:      11,
          fontWeight:    700,
          color:         C.neutralMuted,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom:  16,
        }}>
          Plano de ação
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ActionPlanStep
            number={1}
            text="Verificar os checks reprovados acima e corrigir as configurações dos agentes responsáveis."
          />
          <ActionPlanStep
            number={2}
            text="Revisar os critérios de cobertura e as configurações de segurança (rate limiting, OWASP) antes de reexecutar."
          />
          <ActionPlanStep
            number={3}
            text="Reexecutar a partir do ponto de falha para reaproveitar o progresso já concluído, ou do início se necessário."
          />
        </div>
      </Card>

      {/* ── Ações ───────────────────────────────────────────────────────── */}
      <div style={{
        display:  'flex',
        gap:      12,
        flexWrap: 'wrap',
      }}>
        <Btn
          variant="primary"
          icon={RefreshCw}
          onClick={() => navigate(`liveboard`)}
        >
          Reexecutar do ponto de falha
        </Btn>

        <Btn
          variant="outline"
          icon={RefreshCw}
          onClick={() => navigate('journey')}
        >
          Reexecutar do início
        </Btn>

        <Btn
          variant="ghost"
          size="sm"
          icon={Settings}
          onClick={() => navigate('journey')}
        >
          Ajustar configuração
        </Btn>

        <Btn
          variant="ghost"
          size="sm"
          icon={FileText}
          onClick={() => navigate('output')}
        >
          Ver logs
        </Btn>

        <Btn
          variant="danger"
          size="sm"
          icon={Trash2}
          onClick={() => navigate('dashboard')}
          aria-label="Descartar execução e voltar ao Dashboard"
        >
          Descartar
        </Btn>
      </div>
    </div>
  );
}
